import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  LayoutDashboard,
  LogOut,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';

const cx = (...classes) => classes.filter(Boolean).join(' ');

function isActivePath(pathname, path) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export default function PlatformAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const navItems = [
    {
      id: 'onboarding',
      label: 'Onboarding Requests',
      icon: ClipboardList,
      path: '/platform-admin/onboarding-requests',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-50 w-72 border-r border-slate-200 bg-white">
        <div className="flex h-full min-h-0 flex-col">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-sm">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-base font-bold text-slate-950">
                  PII Platform
                </h1>
                <p className="text-xs font-medium text-orange-700">
                  Platform Admin
                </p>
              </div>
            </div>
          </div>

          <div className="border-b border-slate-200 px-5 py-4">
            <p className="truncate text-sm font-semibold text-slate-900">
              {user?.full_name || 'Platform Admin'}
            </p>
            <p className="truncate text-xs text-slate-500">
              {user?.email}
            </p>
            <div className="mt-3">
              <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
                PLATFORM ADMIN
              </span>
            </div>
          </div>

          <nav className="flex-1 space-y-5 overflow-y-auto px-4 py-5">
            <div>
              <button
                type="button"
                className="mb-2 flex w-full items-center justify-between px-2 text-xs font-bold uppercase tracking-wide text-slate-400"
              >
                Platform Operations
              </button>

              <div className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActivePath(location.pathname, item.path);

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => navigate(item.path)}
                      className={cx(
                        'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition',
                        active
                          ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-100'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                      )}
                    >
                      <Icon
                        className={cx(
                          'h-[18px] w-[18px] shrink-0',
                          active ? 'text-orange-700' : 'text-slate-400 group-hover:text-slate-600',
                        )}
                      />
                      <span>{item.label}</span>
                      {active && <span className="ml-auto h-2 w-2 rounded-full bg-orange-500" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>

          <div className="border-t border-slate-200 p-4">
            <div className="mb-3 rounded-2xl border border-orange-100 bg-orange-50/70 p-3">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-white p-2 text-orange-600 shadow-sm">
                  <LayoutDashboard className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Approval workspace
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Tenant infrastructure is created only after approval.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={logout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
            >
              <LogOut className="h-[18px] w-[18px]" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      <div className="ml-72">
        <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">
              Platform Admin Portal
            </h2>
          </div>
        </div>

        <main className="min-h-[calc(100vh-73px)] p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}